<template>
    <section
        class="popup-container fixed md:container md:mx-auto bottom-0 left-0 right-0 duration-300 pointer-events-none"
        :style="{
            transform: open ? 'none' : 'translateY(calc(100% + var(--popup-shadow-radius)))',
        }"
    >
        <div
            class="popup bg-base-100 p-2 md:p-6 rounded-t-2xl w-screen md:w-3/5 ml-0 md:ml-auto pointer-events-auto border-t-4 border-solid overflow-x-auto"
            :style="{ borderTopColor: statusColor }"
            @click.stop
        >
            <div class="grid md:grid-cols-2 gap-2 flex-wrap md:flex-nowrap">
                <!-- Left column -->
                <div class="flex flex-col flex-nowrap justify-between">
                    <!-- Top left corner -->
                    <div class="flex flex-row items-center mb-3 justify-between md:justify-start">
                        <!-- Room number -->
                        <h2 class="text-4xl mr-1 ml-1 md:ml-0">{{ activeRoom }}</h2>

                        <!-- Badge (popupStatus.badge) -->
                        <div class="flex items-center" v-if="popupStatus.badge">
                            <!-- Current user's room -->
                            <span
                                class="badge badge-success mx-1 whitespace-nowrap"
                                v-if="popupStatus.badge === 'OWNED_ROOM'"
                            >
                                A te szobád
                            </span>

                            <!-- Current user has to make a decision -->
                            <span
                                class="badge badge-warning mx-1 whitespace-nowrap"
                                v-else-if="popupStatus.badge === 'AWAIT_USER'"
                                title=""
                            >
                                Elfogadásodra vár!
                            </span>

                            <!-- Roommate has to make a decision -->
                            <span
                                class="badge badge-warning mx-1 whitespace-nowrap"
                                v-else-if="popupStatus.badge === 'AWAIT_ROOMMATE'"
                                title=""
                            >
                                Szobatárs megerősítésére vár!
                            </span>
                        </div>
                    </div>

                    <!-- Bottom left corner (popupStatus.personInfo) -->
                    <div class="popup-grid grid-flow-col">
                        <!-- Space silhouettes -->
                        <template v-for="index in 2" :key="index">
                            <!-- Space is taken icon -->
                            <AccountFullIcon
                                v-if="count > index - 1 && popupStatus.personInfo"
                                :sex="currentRoomInfo?.occupants[index - 1]?.sex"
                            />
                            <!-- Space is not taken icon -->
                            <AccountIcon v-else/>
                        </template>

                        <template v-if="popupStatus.personInfo">
                            <!-- Both names -->
                            <template v-if="popupStatus.personInfo === 'OCCUPANTS'">
                                <template v-for="index in 2" :key="index">
                                    <h2
                                        v-if="currentRoomInfo?.occupants[index - 1]"
                                        class="text-3xl md:whitespace-nowrap"
                                    >
                                        {{ currentRoomInfo.occupants[index - 1].name }}
                                    </h2>

                                    <h2
                                        v-else-if="currentRoomInfo.occupants"
                                        :key="index"
                                        class="text-2xl italic md:whitespace-nowrap"
                                        style="color: var(--base-content-hint)"
                                    >
                                        üres férőhely
                                    </h2>
                                </template>
                            </template>

                            <!-- Decision needed -->
                            <template
                                v-if="
                                    popupStatus.personInfo === 'ACCEPT_DENY_INVITE' ||
                                    popupStatus.personInfo === 'ACCEPT_DENY_APPLICANT'
                                "
                            >
                                <!-- Decide invite text -->
                                <template v-if="popupStatus.personInfo === 'ACCEPT_DENY_INVITE'">
                                    <div class="text-l whitespace-nowrap ml-2">
                                        {{
                                            currentRoomInfo.occupants?.length === 2
                                                ? currentRoomInfo.occupants[1].name
                                                : ''
                                        }}
                                        meghívott a szobába!
                                    </div>
                                </template>
                                <!-- Decide applicant text -->
                                <template v-if="popupStatus.personInfo === 'ACCEPT_DENY_APPLICANT'">
                                    <div class="text-l whitespace-nowrap ml-2">
                                        {{
                                            currentRoomInfo.occupants?.length === 2
                                                ? currentRoomInfo.occupants[1].name
                                                : ''
                                        }}
                                        jelentkezett melléd!
                                    </div>
                                </template>
                                <!-- Decide button box -->
                                <div class="btn-group flex-nowrap w-max">
                                    <button
                                        class="btn btn-success"
                                        @click.stop="accept"
                                        :disabled="!open"
                                    >
                                        Elfogadás
                                    </button>
                                    <button
                                        class="btn btn-error"
                                        @click.stop="decline"
                                        :disabled="!open"
                                    >
                                        Elutasítás
                                    </button>
                                </div>
                            </template>

                            <!-- Reserve with roommate -->
                            <template v-if="popupStatus.personInfo === 'DOUBLE_RESERVE'">
                                <div class="text-l whitespace-nowrap ml-2">
                                    Foglalás szobatárs Neptunkóddal
                                </div>

                                <div class="transition-all transition-300">
                                    <form class="form-control">
                                        <label class="input-group flex-nowrap">
                                            <input
                                                id="roommate"
                                                type="text"
                                                placeholder="NEPTUN"
                                                class="input input-bordered w-min max-w-xs"
                                                v-model="roommate"
                                                style="outline: none"
                                                :disabled="!open"
                                                @input.stop="validateNeptun"
                                                @keyup.stop="validateNeptun"
                                            />
                                            <button
                                                class="btn btn-success"
                                                @click.prevent="reserve"
                                                :disabled="roommate.length !== 6 || !open"
                                            >
                                                Foglalás
                                            </button>
                                        </label>
                                    </form>
                                </div>
                            </template>

                            <!-- Invite with neptun -->
                            <template v-if="popupStatus.personInfo === 'INVITE'">
                                <div class="text-l whitespace-nowrap ml-2">
                                    Szobatárs meghívása Neptunkóddal
                                </div>
                                <div class="transition-all transition-300">
                                    <form class="form-control">
                                        <label class="input-group flex-nowrap">
                                            <input
                                                id="roommate"
                                                type="text"
                                                placeholder="NEPTUN"
                                                class="input input-bordered w-min max-w-xs"
                                                v-model="roommate"
                                                style="outline: none"
                                                :disabled="!open"
                                                @input.stop="validateNeptun"
                                                @keyup.stop="validateNeptun"
                                            />
                                            <button
                                                class="btn btn-success"
                                                @click.prevent="invite"
                                                :disabled="roommate.length !== 6 || !open"
                                            >
                                                Meghívás
                                            </button>
                                        </label>
                                    </form>
                                </div>
                            </template>

                            <!-- Apply -->
                            <template v-if="popupStatus.personInfo === 'APPLY'">
                                <!-- Occupant name -->
                                <h2 class="text-3xl md:whitespace-nowrap">
                                    {{
                                        currentRoomInfo &&
                                        currentRoomInfo.occupants &&
                                        currentRoomInfo.occupants[0]
                                            ? currentRoomInfo.occupants[0].name
                                            : ''
                                    }}
                                </h2>

                                <!-- Application button -->
                                <button
                                    class="btn btn-warning"
                                    @click.stop="apply"
                                    :disabled="!open"
                                >
                                    Jelentkezés
                                </button>
                            </template>

                            <!-- Text if room is empty -->
                            <template v-if="popupStatus.personInfo === 'EMPTY_TEXT'">
                                <h2
                                    v-for="index in 2"
                                    :key="index"
                                    class="text-2xl italic md:whitespace-nowrap"
                                    style="color: var(--base-content-hint)"
                                >
                                    üres férőhely
                                </h2>
                            </template>
                        </template>
                    </div>
                </div>

                <!-- Right column (description, extra mini-text, close button) -->
                <div class="grid grid-cols-1 justify-items-end">
                    <!-- Description -->
                    <p class="mb-4 md:text-justify self-start" v-if="currentRoom?.description">
                        {{ currentRoom.description }}
                    </p>

                    <!-- Bottom right corner (popupStatus.extraSpace) -->
                    <div class="flex flex-row items-end self-end justify-between md:justify-end">
                        <template v-if="popupStatus.extraSpace">
                            <!-- Texts -->
                            <!-- Not logged in text -->
                            <span
                                v-if="popupStatus.extraSpace === 'NO_LOGIN'"
                                class="self-center text-sm text-gray-400"
                            >
                                <a href="/login" class="underline cursor-pointer">
                                    Be kell jelentkezned
                                </a>
                                a szobafoglaláshoz.
                            </span>
                            <!-- Is invited to a different room already -->
                            <span
                                v-if="popupStatus.extraSpace === 'INVITED_TO_OTHER'"
                                class="self-center text-sm text-gray-400"
                            >
                                Már meghívtak a
                                <button @click="openRoom" class="underline" :disabled="!open">
                                    {{ user.room_number }}
                                </button>
                                szobába.
                            </span>
                            <!-- Has applied to a different room already -->
                            <span
                                v-if="popupStatus.extraSpace === 'APPLIED_TO_OTHER'"
                                class="self-center text-sm text-gray-400"
                            >
                                Már jelentkeztél a
                                <button @click="openRoom" class="underline" :disabled="!open">
                                    {{ user.room_number }}
                                </button>
                                szobába.
                            </span>
                            <!-- Has a different room already -->
                            <span
                                v-if="popupStatus.extraSpace === 'HAS_OTHER_ROOM'"
                                class="self-center text-sm text-gray-400"
                            >
                                Már lefoglaltad a
                                <button @click="openRoom" class="underline" :disabled="!open">
                                    {{ user.room_number }}
                                </button>
                                szobát.
                            </span>

                            <!-- Buttons -->
                            <div class="btn-group flex-nowrap">
                                <!-- Cancel invite button -->
                                <button
                                    v-if="
                                        popupStatus.extraSpace === 'CANCEL_INVITE' ||
                                        popupStatus.extraSpace === 'CANCEL_INVITE_LEAVE_ROOM'
                                    "
                                    class="btn btn-info"
                                    @click.stop="cancelInvite"
                                    :disabled="!open"
                                >
                                    Meghívás törlése
                                </button>

                                <!-- Leave room button -->
                                <modal
                                    v-if="
                                        popupStatus.extraSpace === 'LEAVE_ROOM' ||
                                        popupStatus.extraSpace === 'CANCEL_INVITE_LEAVE_ROOM'
                                    "
                                    :label="'Lemondás'"
                                    :title="'Biztosan le szeretnéd mondani a szobát?'"
                                    :button-class="'btn btn-error'"
                                    :disabled="!open"
                                    :yes="'Igen, biztosan'"
                                    :no="'Mégse'"
                                    @submit="cancel"
                                    class="ml-2"
                                >
                                    Ha a szobatársadnak nincs előjoga erre a szobára, akkor ezzel ő
                                    is kikerül a szobából!
                                </modal>
                                <!-- Cancel application button -->
                                <button
                                    v-if="popupStatus.extraSpace === 'CANCEL_APPLICATION'"
                                    class="btn btn-info"
                                    @click.stop="cancelInvite"
                                    :disabled="!open"
                                >
                                    Jelentkezés törlése
                                </button>
                            </div>
                        </template>

                        <!-- Close button -->
                        <div class="ml-auto">
                            <button
                                class="btn btn-square md:btn ml-2"
                                @click.stop="closePopup"
                                :disabled="!open"
                            >
                                <CloseIcon/>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import {mapActions, mapGetters, mapState} from 'vuex';
import floors from '../../store/constants/floors';
import Modal from '../layout/Modal';
import CloseIcon from '../svg/CloseIcon';
import AccountFullIcon from '../svg/AccountFullIcon';
import AccountIcon from '../svg/AccountIcon';

export default {
    name: 'PopupComponent',
    components: {AccountIcon, AccountFullIcon, CloseIcon, Modal},
    data() {
        return {
            roommate: '',
        };
    },
    computed: {
        ...mapGetters('Popup', ['activeRoom', 'activeRoomInfo', 'open']),
        ...mapGetters('Room', ['rooms']),
        ...mapState('User', ['user']),
        floorRooms() {
            return Object.values(floors).flatMap(({north, south}) => [...north, ...south]);
        },
        currentRoom() {
            return this.floorRooms.find((room) => room.roomNumber === this.activeRoom);
        },
        count() {
            return this.rooms[this.activeRoom]?.count ?? null;
        },
        popupStatus() {
            let ui = {
                badge: null, // null, AWAIT_ROOMMATE, AWAIT_USER, OWNED_ROOM
                personInfo: null, // null, OCCUPANTS, ACCEPT_DENY_INVITE, ACCEPT_DENY_APPLICANT, DOUBLE_RESERVE, INVITE, APPLY, EMPTY_TEXT
                extraSpace: null, // null, LEAVE_ROOM, CANCEL_INVITE, CANCEL_APPLICATION, CANCEL_INVITE_LEAVE_ROOM, APPLIED_TO_OTHER, INVITED_TO_OTHER, HAS_OTHER_ROOM, NO_LOGIN
            };

            if (!this.user) {
                // NO_LOGIN
                return {extraSpace: 'NO_LOGIN'};
            }

            if (!this.user.room_number) {
                switch (this.count) {
                    case 0: // EMPTY
                        return {personInfo: 'DOUBLE_RESERVE'};
                    case 1: // HALF_EMPTY_FREE
                        return {personInfo: 'APPLY'};
                    case 2: // FULL_OTHER
                        return {personInfo: 'OCCUPANTS'};
                }
            }

            if (
                this.user.reservation_status === 'RESERVED' ||
                this.user.reservation_status === 'ASSIGNED'
            ) {
                if (this.activeRoom === this.user.room_number) {
                    if (this.count === null) {
                        return {
                            badge: 'OWNED_ROOM',
                            personInfo: 'OCCUPANTS',
                            extraSpace: 'LEAVE_ROOM',
                        };
                    }
                    if (this.count === 1) {
                        if (this.user.reservation_status === 'RESERVED') {
                            // HALF_EMPTY_RESERVED
                            return {
                                badge: 'OWNED_ROOM',
                                personInfo: 'INVITE',
                                extraSpace: 'LEAVE_ROOM',
                            };
                        }
                        // HALF_EMPTY_ASSIGNED
                        return {badge: 'OWNED_ROOM', personInfo: 'INVITE'};
                    }
                    if (this.count === 2) {
                        if (this.user.roommate_status === 'INVITED') {
                            if (this.user.reservation_status === 'RESERVED') {
                                // HALF_EMPTY_RESERVED_ROOMATE_INVITED
                                return {
                                    badge: 'OWNED_ROOM',
                                    personInfo: 'OCCUPANTS',
                                    extraSpace: 'CANCEL_INVITE_LEAVE_ROOM',
                                };
                            }

                            // HALF_EMPTY_ASSIGNED_ROOMATE_INVITED
                            return {
                                badge: 'OWNED_ROOM',
                                personInfo: 'OCCUPANTS',
                                extraSpace: 'CANCEL_INVITE',
                            };
                        }
                        if (this.user.roommate_status === 'APPLIED') {
                            if (this.user.reservation_status === 'RESERVED') {
                                // HALF_EMPTY_RESERVED_ROOMATE_APPLIED
                                return {
                                    badge: 'OWNED_ROOM',
                                    personInfo: 'ACCEPT_DENY_APPLICANT',
                                    extraSpace: 'LEAVE_ROOM',
                                };
                            }
                            // HALF_EMPTY_ASSIGNED_ROOMATE_APPLIED
                            return {badge: 'OWNED_ROOM', personInfo: 'ACCEPT_DENY_APPLICANT'};
                        }
                        if (this.user.reservation_status === 'RESERVED') {
                            // FULL_RESERVED
                            return {
                                badge: 'OWNED_ROOM',
                                personInfo: 'OCCUPANTS',
                                extraSpace: 'LEAVE_ROOM',
                            };
                        }
                        // FULL_ASSIGNED
                        return {badge: 'OWNED_ROOM', personInfo: 'OCCUPANTS'};
                    }
                }
                switch (this.count) {
                    case 0: // EMPTY_ALREADY_HAS_OTHER_ROOM
                        return {personInfo: 'EMPTY_TEXT', extraSpace: 'HAS_OTHER_ROOM'};
                    case 1: // HALF_EMPTY_ALREADY_HAS_OTHER_ROOM
                        return {personInfo: 'OCCUPANTS', extraSpace: 'HAS_OTHER_ROOM'};
                    case 2: // FULL_ALREADY_HAS_OTHER_ROOM
                        return {personInfo: 'OCCUPANTS', extraSpace: 'HAS_OTHER_ROOM'};
                }
            }
            //INVITED
            if (this.user.reservation_status === 'INVITED') {
                if (this.activeRoom === this.user.room_number) {
                    if (this.user.neptun === this.user.reserved_by) {
                        // EMPTY_INVITED_ROOMMATE_INVITED
                        return {
                            badge: 'AWAIT_ROOMMATE',
                            personInfo: 'OCCUPANTS',
                            extraSpace: 'LEAVE_ROOM',
                        };
                    }
                    // HALF_EMPTY_INVITED_TO
                    return {badge: 'AWAIT_USER', personInfo: 'ACCEPT_DENY_INVITE'};
                }

                // Correct extraSpace text if reserved empty with roommate neptun (3 extra, hidden states)
                // DB has INVITED status, but we should display APPLIED_TO_OTHER status to self
                const extraSpace =
                    this.user.neptun === this.user.reserved_by
                        ? 'APPLIED_TO_OTHER'
                        : 'INVITED_TO_OTHER';

                switch (this.count) {
                    case 0: // EMPTY_INVITED_TO_OTHER
                        return {personInfo: 'EMPTY_TEXT', extraSpace: extraSpace};
                    case 1: // HALF_EMPTY_INVITED_TO_OTHER
                        return {personInfo: 'OCCUPANTS', extraSpace: extraSpace};
                    case 2: // FULL_INVITED_TO_OTHER
                        return {personInfo: 'OCCUPANTS', extraSpace: extraSpace};
                }
            }
            //APPLIED
            if (this.user.reservation_status === 'APPLIED') {
                if (this.activeRoom === this.user.room_number) {
                    // HALF_EMPTY_APPLIED_TO
                    return {
                        badge: 'AWAIT_ROOMMATE',
                        personInfo: 'OCCUPANTS',
                        extraSpace: 'CANCEL_APPLICATION',
                    };
                }
                switch (this.count) {
                    case 0: // EMPTY_APPLIED_TO_OTHER
                        return {personInfo: 'EMPTY_TEXT', extraSpace: 'APPLIED_TO_OTHER'};
                    case 1: // HALF_EMPTY_APPLIED_TO_OTHER
                        return {personInfo: 'OCCUPANTS', extraSpace: 'APPLIED_TO_OTHER'};
                    case 2: // FULL_APPLIED_TO_OTHER
                        return {personInfo: 'OCCUPANTS', extraSpace: 'APPLIED_TO_OTHER'};
                }
            }

            return ui;
        },
        statusColor() {
            if (this.user && this.user.room_number === this.activeRoom) {
                return 'var(--room-reserved)';
            }
            switch (this.count) {
                case 0:
                    return 'var(--room-empty)';
                case 1:
                    return 'var(--room-half)';
                case 2:
                    return 'var(--room-full)';
            }
            return 'var(--room-disabled)';
        },
        // PLACEHOLDER TODO REMOVE
        currentRoomInfo() {
            return {occupants: this.activeRoomInfo ?? []};
        },
        // END PLACEHOLDER
    },
    methods: {
        ...mapActions('Room', ['switchFloor']),
        ...mapActions('Popup', [
            'selectRoom',
            'reserveRoom',
            'cancelRoom',
            'applyToRoom',
            'inviteToRoom',
            'acceptRoom',
            'declineRoom',
            'declineRoommate',
        ]),
        ...mapActions('User', ['setUserRoom']),
        closePopup() {
            this.selectRoom(null);
        },
        async reserve() {
            const success = await this.reserveRoom({
                room: this.activeRoom,
                roommate: this.roommate,
            });
            if (success) {
                this.roommate = '';
            }
        },
        accept() {
            if (this.user.reservation_status === 'INVITED') {
                return this.acceptRoom(this.user.reservation_id);
            }
            if (this.user.roommate_status === 'APPLIED') {
                return this.acceptRoom(this.user.roommate_reservation_id);
            }
        },
        apply() {
            this.applyToRoom(this.activeRoom);
        },
        cancel() {
            this.cancelRoom();
        },
        decline() {
            if (
                this.user.reservation_status === 'INVITED' ||
                this.user.reservation_status === 'APPLIED'
            ) {
                return this.declineRoom(this.user.reservation_id);
            }

            if (
                this.user.roommate_status === 'INVITED' ||
                this.user.roommate_status === 'APPLIED'
            ) {
                return this.declineRoommate(this.user.roommate_reservation_id);
            }
        },
        invite() {
            this.inviteToRoom({room: this.activeRoom, roommate: this.roommate});
        },
        cancelInvite() {
            this.decline();
        },
        openRoom() {
            if (this.user.room_number) {
                this.selectRoom(this.user.room_number);
                this.switchFloor(this.user.room_number[0]);
            }
        },
        validateNeptun(event) {
            this.roommate = (this.roommate.match(/[a-zA-Z0-9]/g)?.join('') ?? '')
                .substring(0, 6)
                .toUpperCase();
            return this.roommate?.length === 6;
        },
    },
};
</script>

<style scoped>
.popup-container {
    --popup-shadow-radius: 20px;
}

.popup {
    box-shadow: 0 0 var(--popup-shadow-radius) 0 rgba(0, 0, 0, 0.3);
}

.popup .popup-grid {
    display: grid;
    grid-template-columns: min-content 1fr;
    grid-template-rows: 1fr 1fr;
    align-items: center;
}
</style>
